import React, { useState, useEffect, useContext, useRef, useMemo } from 'react'
import injectStyles from 'react-jss'
import moment from 'moment'

import BreadcrumbsAIRoster from './components/BreadcrumbsAIRoster'
import ProgressSteps from './components/ProgressSteps'
import { styles } from './styles'
import {
  STEPS,
  TIME_SLOTS,
  DEFAULT_BA_PER_MISSION,
  DEFAULT_BA_PAY_ADJUSTMENT,
} from './options'
import StepDate from './pages/StepDate/StepDate'
import StepResults from './pages/StepResults'
import classNames from 'classnames'
import StepLocation from './pages/StepLocation'
import ButtonV2 from 'Components/Buttons/ButtonV2'
import ModalV2 from '../../../Components/ModalV2'
import WantToLeaveIcon from './icons/WantToLeaveIcon'
import GeneratingIcon from './icons/GeneratingIcon'
import { SnackBarContext } from 'Components/SnackBar'
import {
  getPayloadForAI,
  getMissionTimeslot,
  formatLocations,
  generateButtonLabel,
  nextButtonAvailable,
  splitMissionsByChunksForTLA,
  simplifyWeatherData,
  buildFeedbackOutput,
  createWeatherFetcherWithRetry,
  mapWithConcurrency,
  prepareSkippedLocations,
} from './utils'
import { getWeatherForLocationInRange } from 'Utils/Weather'
import { useWrappedPosthog } from 'Components/PostHogProvider/hooks'
import { AnalyticsEventEmitter } from 'Utils/Analytics/AnalyticsEventEmitter'
import { authorization } from 'Module/Auth/Auth'
import {
  getLocationInPostcodesAndCvrPercentiles,
  createMissions,
  getLoactionByAI,
} from './api'
import { convertArrayToHashMap } from '../../../shared/libraries/dataSets'
import { MISSION_ENUM_TYPES } from '../../../shared/constants'

const AIRoster = ({
  campaign,
  city,
  cityPlan,
  history,
  classes,
  onGenerateMissionsCallback,
}) => {
  const runSnackBar = useContext(SnackBarContext)
  const posthog = useWrappedPosthog()

  const [step, setStep] = useState(STEPS.DATE)
  const [selectedDates, setSelectedDates] = useState([])
  const [loading, setLoading] = useState(false)
  const [postcodesWithLocations, setPostcodesWithLocations] = useState([])
  const [isDistributionZoneExists, setIsDistributionZoneExists] = useState(
    false,
  )
  const [selectedPostcodes, setSelectedPostcodes] = useState([])
  const [whitelistedLocations, setWhitelistedLocations] = useState([])
  const [preparedMissionsDatesData, setPreparedMissionsDatesData] = useState([])
  const [modalProps, setModalProps] = useState({
    isOpen: false,
  })
  const [cvrPercentiles, setCvrPercentiles] = useState({})
  const [allLocationsHashMap, setAllLocationsHashMap] = useState({})
  const [dataBeforeMissionCreation, setDataBeforeMissionCreation] = useState([])
  const [isEditResultsStep, setIsEditResultsStep] = useState(false)
  const [, setProgress] = useState({
    text: '',
    percentage: 0,
  })
  const unblockRef = useRef(null)
  const [aiInputPayload, setAiInputPayload] = useState(null)
  const [aiRawOutput, setAiRawOutput] = useState(null)
  const [aiRequestId, setAiRequestId] = useState(null)
  const [skippedLocations, setSkippedLocations] = useState([])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getLocationInPostcodesAndCvrPercentiles(city.id)

      const {
        data: {
          city: {
            postcodesAndLocationsInsideDistributionZones: {
              postcodes,
              isDistributionZoneExists,
            },
            cvrPercentiles,
          },
        },
      } = await response.json()

      setCvrPercentiles(cvrPercentiles)

      setPostcodesWithLocations(
        postcodes.sort((a, b) => b.locations.length - a.locations.length),
      )
      const locations = formatLocations(postcodes)

      setAllLocationsHashMap(convertArrayToHashMap(locations, 'id'))
      setIsDistributionZoneExists(isDistributionZoneExists)
    } catch (error) {
      setModalProps({
        isOpen: true,
        title: 'Error fetching locations data',
        description: <span>Something went wrong. Please notify support.</span>,
        onClose: onCloseModal,
      })

      console.error('Error in fetchData:', JSON.stringify(error))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const changesWasMade = preparedMissionsDatesData.length > 0

  // show alert if user tries to close the page or refresh the page
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (changesWasMade) {
        e.preventDefault()
        e.returnValue =
          'Are you sure you want to leave? Your changes will be lost.'
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [changesWasMade])

  // // show alert if user tries to navigate away from the page
  useEffect(() => {
    const unblock = history.block((targetLocation, action) => {
      // Check current state, not the state from when effect was created
      const currentChangesWasMade = preparedMissionsDatesData.length > 0
      if (currentChangesWasMade) {
        setModalProps({
          isOpen: true,
          title: 'Are you sure you want to leave?',
          description: (
            <span>
              You haven't finished setting up your missions. If <br /> you leave
              now, your changes won't be saved.
            </span>
          ),
          icon: <WantToLeaveIcon />,
          onClose: onCloseModal,
          buttons: [
            {
              label: 'Cancel',
              onClick: onCloseModal,
              outlined: true,
            },
            {
              label: 'Confirm',
              onClick: () => {
                unblock()
                history.push(targetLocation.pathname)
              },
              outlined: false,
            },
          ],
        })
        return false
      }
      return true
    })

    unblockRef.current = unblock

    return () => {
      unblock()
    }
  }, [preparedMissionsDatesData.length])

  const extednAIData = async ({
    aiData,
    preparedMissionsDatesData,
    allLocationsHashMap,
    city,
  }) => {
    if (
      !Object.keys(allLocationsHashMap).length ||
      !aiData.length ||
      !preparedMissionsDatesData.length ||
      !city
    ) {
      return []
    }

    // Create fetcher with retry logic using shared utility
    const fetchWeatherWithRetry = createWeatherFetcherWithRetry({
      fetchFunction: async params => {
        const data = await getWeatherForLocationInRange(params)

        // Process and return weather data
        if (data && Array.isArray(data.days) && data.days.length > 0) {
          const simplified = simplifyWeatherData(data.days)
          return simplified[0] || null
        }
        return null
      },
      buildCacheKey: params =>
        `${params.latitude},${params.longitude},${params.from}`,
      validateResponse: weather => weather !== null,
    })

    // Process days with limited concurrency to avoid overwhelming weather API
    // Limit to 1 concurrent day, each with 1 concurrent location request = max 1 total request
    const result = await mapWithConcurrency(
      aiData,
      1, // max concurrent days being processed
      async item => {
        const { day, locations } = item

        const data = preparedMissionsDatesData.find(item => item.id === day)
        if (!data) {
          console.error(`Date ${day} not found in preparedMissionsDatesData`)
          return null
        }

        let morningMissions = 0
        let lunchMissions = 0
        let afternoonMissions = 0

        // Limit concurrent weather requests per day to reduce rate limiting
        const missions = await mapWithConcurrency(
          locations,
          1, // max 1 concurrent request to avoid 429 errors
          async item => {
            const locationTime = item.time // format '10:00'

            const timeSlot = getMissionTimeslot({ city, time: locationTime })

            if (timeSlot === TIME_SLOTS.MORNING) {
              morningMissions++
            } else if (timeSlot === TIME_SLOTS.LUNCH) {
              lunchMissions++
            } else {
              afternoonMissions++
            }

            const location = allLocationsHashMap[item.id]

            if (!location) {
              console.error(`Location with id ${item.id} not found in hashMap`)
              return null
            }

            try {
              const weather = await fetchWeatherWithRetry({
                latitude: location.latitude,
                longitude: location.longitude,
                from: day,
                to: day,
                cityId: city.id,
              })

              return {
                locationId: location.id,
                name: location.name,
                time: item.time, // "08:45" -> "08:45 AM - 11:45 AM"
                basPerMission: DEFAULT_BA_PER_MISSION,
                weather: weather || null,
                predictedCvr: item.predictedCvr
                  ? (item.predictedCvr * 100).toFixed(2)
                  : null,
                predictedCvrUnformatted: item.predictedCvr,
                timeSlot,
                isSheltered: location.isSheltered,
                isEvent: location.isEvent,
                draftPayAdjustment: DEFAULT_BA_PAY_ADJUSTMENT,
                aiOutputData: {
                  ...item,
                  day,
                },
              }
            } catch (error) {
              console.error('Error getting weather for location:', error)
              return {
                locationId: location.id,
                name: location.name,
                time: item.time,
                basPerMission: DEFAULT_BA_PER_MISSION,
                weather: null, // fallback
                predictedCvr: item.predictedCvr
                  ? (item.predictedCvr * 100).toFixed(2)
                  : null,
                predictedCvrUnformatted: item.predictedCvr,
                timeSlot,
                isSheltered: location.isSheltered,
                isEvent: location.isEvent,
                draftPayAdjustment: DEFAULT_BA_PAY_ADJUSTMENT,
                aiOutputData: {
                  ...item,
                  day,
                },
              }
            }
          },
        )

        return {
          id: data.id,
          date: data.date,
          morningMissions,
          lunchMissions,
          afternoonMissions,
          details: true,
          missions,
        }
      },
    )

    return result
  }

  const handleSendRequestToAI = async ({
    selectedDates,
    postcodesWithLocations,
    selectedPostcodes,
    whitelistedLocations,
    preparedMissionsDatesData,
  }) => {
    try {
      const payload = await getPayloadForAI({
        city,
        campaign,
        selectedDates,
        preparedMissionsDatesData,
        selectedPostcodes,
        postcodesWithLocations,
        whitelistedLocations,
      })

      const response = await getLoactionByAI(payload)

      if (
        response.status === 'success' ||
        response.message.includes('Skipped whitelisted locations')
      ) {
        return {
          aiData: response.daySchedules,
          payload,
          requestId: response.request_id || response.requestId || null,
          message: response.message || '',
        }
      }

      console.error('FETCHING AI DATA NOT SUCCESS', JSON.stringify(response))

      return null
    } catch (error) {
      console.error('FETCHING AI DATA ERROR', JSON.stringify(error))

      return null
    }
  }

  const navigateToResults = async () => {
    setModalProps({
      isOpen: true,
      title: 'Generating Missions',
      description: (
        <span>
          Please wait while we are generating your missions <br />
          and predicting the CVRs.
        </span>
      ),
      icon: <GeneratingIcon />,
      isHideCloseButton: true,
    })

    try {
      const aiResult = await handleSendRequestToAI({
        selectedDates,
        postcodesWithLocations,
        selectedPostcodes,
        whitelistedLocations,
        preparedMissionsDatesData,
      })

      if (!aiResult) {
        onCloseModal()

        setModalProps({
          isOpen: true,
          title: 'Error getting AI data',
          description: (
            <span>Something went wrong. Please notify support.</span>
          ),
          onClose: onCloseModal,
        })

        setLoading(false)
        return
      }

      const { aiData, payload: usedPayload, requestId, message } = aiResult

      setSkippedLocations(prepareSkippedLocations(message, allLocationsHashMap))

      const dataForStepResults = await extednAIData({
        aiData,
        preparedMissionsDatesData,
        allLocationsHashMap,
        city,
      })

      // Check if we received data
      if (!dataForStepResults || dataForStepResults.length === 0) {
        onCloseModal()

        setModalProps({
          isOpen: true,
          title: 'Error extending AI data',
          description: (
            <span>Something went wrong. Please notify support.</span>
          ),
          onClose: onCloseModal,
        })

        setLoading(false)

        return
      }

      // Set generated data
      setDataBeforeMissionCreation(dataForStepResults)
      setAiInputPayload(usedPayload)
      setAiRawOutput(aiData)
      setAiRequestId(requestId)
      setLoading(false)

      onCloseModal()
      setStep(STEPS.RESULTS)
    } catch (error) {
      console.error('Error in navigateToResults:', error)

      onCloseModal()

      setModalProps({
        isOpen: true,
        title: 'Error getting AI data',
        description: <span>Something went wrong. Please try again.</span>,
        onClose: onCloseModal,
      })

      setLoading(false)
    }
  }

  const onProgress = ({ processedAmount, totalAmount }) => {
    const percentage = Math.round((processedAmount / totalAmount) * 100)
    const progressText = `${processedAmount} from ${totalAmount} missions created (${percentage}%)`

    setProgress({
      percentage,
      text: progressText,
    })

    // Update modal with current progress
    setModalProps({
      isOpen: true,
      title: 'Creating missions',
      description: (
        <span>
          Please wait while we are generating your missions
          <br />
          <br />
          <span>{progressText}</span>
        </span>
      ),
      icon: <GeneratingIcon />,
      isHideCloseButton: true,
    })
  }

  const hadleCreateMissions = async () => {
    setProgress({
      percentage: 0,
      text: 'Preparing missions...',
    })

    // Initial modal setup - will be updated by onProgress
    setModalProps({
      isOpen: true,
      title: 'Creating missions',
      description: (
        <span>
          Please wait while we are generating your missions
          <br />
          <br />
          <span>Preparing missions...</span>
        </span>
      ),
      icon: <GeneratingIcon />,
      isHideCloseButton: true,
    })

    const missionsToGenerate = dataBeforeMissionCreation.reduce((acc, day) => {
      if (day.missions.length) {
        day.missions.forEach(mission => {
          const dateTime = `${day.id}T${mission.time}:00.000`
          const dataForMissionCreation = {
            dateTime,
            cityPlanId: cityPlan.id,
            locationId: mission.locationId,
            draftPayAdjustment: mission.draftPayAdjustment,
            type: MISSION_ENUM_TYPES.HANDTOHAND,
            automatic: true,
            cvrPredictionPercent: mission.predictedCvrUnformatted,
          }

          for (let i = 1; i <= mission.basPerMission; i++) {
            acc.push(dataForMissionCreation)
          }
        })
      }

      return acc
    }, [])

    setProgress({
      text: 'Grouping missions...',
      percentage: 0,
    })

    const missionsByChunks = splitMissionsByChunksForTLA(missionsToGenerate)

    if (missionsByChunks.some(chunk => chunk.length >= 200)) {
      onCloseModal()

      setModalProps({
        isOpen: true,
        title: 'Validation error',
        onClose: onCloseModal,
        description: (
          <span>
            We are sorry, but we are not able to generate more than 200 missions
            with one location and time slot at once.
            <br />
            <br />
            <span>Please try to generate less missions.</span>
          </span>
        ),
      })

      return
    }

    try {
      const createdMissions = await createMissions({
        missionsByChunks,
        campaignId: campaign.id,
        onProgress,
        totalAmount: missionsToGenerate.length,
      })

      onCloseModal()

      onGenerateMissionsCallback(createdMissions)

      // Temporarily unblock navigation before redirect
      if (unblockRef.current) {
        unblockRef.current()
      }

      const weekNumber = moment(selectedDates[0]).format('W')
      const year = selectedDates[0].split('-')[0]

      const feedbackOutput = buildFeedbackOutput({
        aiRawOutput,
        dataBeforeMissionCreation,
      })

      if (feedbackOutput.length > 0) {
        const metadata = {
          input: aiInputPayload,
          output: aiRawOutput,
          feedback: feedbackOutput,
        }
        // Send to PostHog instead of API
        try {
          const user = authorization.getUser()
          const emitter = new AnalyticsEventEmitter(posthog)
          emitter.run({
            eventName: 'ai_roster_feedback_submitted',
            userId: user.id,
            clientName: campaign.client.name,
            campaignId: String(campaign.id),
            additionalPayload: {
              requestId: aiRequestId || null,
              metadata: JSON.stringify(metadata),
              cityId: city.id,
              cityName: city.name,
            },
          })
        } catch (e) {
          console.error('PostHog emit error', e)
        }
      }

      history.push(
        window.location.pathname.replace(
          '/ai-roster',
          `/rosters/${year}-${weekNumber}`,
        ),
      )

      runSnackBar({
        vertical: 'top',
        type: 'success',
        msg: 'Missions generated!',
      })
    } catch (error) {
      setModalProps({
        isOpen: true,
        title: 'Error generating missions',
        description: <span>{JSON.stringify(error)}</span>,
      })
      console.error('Error in hadleCreateMissions:', JSON.stringify(error))
      runSnackBar({
        vertical: 'top',
        type: 'error',
        msg: 'Error generating missions',
      })
    }
  }

  const onCloseModal = () => {
    setModalProps(prev => ({ ...prev, isOpen: false }))
  }

  const onBack = () => {
    if (step === STEPS.DATE) {
      return history.push(window.location.pathname.replace('/ai-roster', ''))
    } else if (step === STEPS.LOCATION) {
      setStep(STEPS.DATE)
    } else if (step === STEPS.RESULTS) {
      setStep(STEPS.LOCATION)
    }
  }

  const onNext = async () => {
    if (step === STEPS.DATE) {
      setStep(STEPS.LOCATION)
    } else if (step === STEPS.LOCATION) {
      await navigateToResults()
    } else if (step === STEPS.RESULTS) {
      await hadleCreateMissions()
    }
  }

  const isNextButtonAvailable = useMemo(() => {
    return nextButtonAvailable({
      step,
      preparedMissionsDatesData,
      isEditResultsStep,
      dataBeforeMissionCreation,
    })
  }, [
    step,
    preparedMissionsDatesData,
    isEditResultsStep,
    dataBeforeMissionCreation,
  ])

  const nextButtonLabel = useMemo(() => {
    return generateButtonLabel(step)
  }, [step])

  const locations = useMemo(() => {
    return Object.values(allLocationsHashMap)
  }, [allLocationsHashMap])

  return (
    <div className={classes.page}>
      <BreadcrumbsAIRoster
        campaign={campaign}
        city={city}
        cityPlan={cityPlan}
      />
      <div className={classNames(classes.title, classes.centeredContent)}>
        Generate Missions
      </div>
      {/* heaader */}
      <div className={classes.header}>
        <ButtonV2
          label="BACK"
          outlined={false}
          onClick={onBack}
          disabled={false}
        />
        <ProgressSteps currentStep={step} />
        <ButtonV2
          label={nextButtonLabel}
          onClick={onNext}
          disabled={!isNextButtonAvailable}
        />
      </div>
      {step === STEPS.DATE ? (
        <StepDate
          classes={classes}
          setSelectedDates={setSelectedDates}
          selectedDates={selectedDates}
          preparedData={preparedMissionsDatesData}
          setPreparedData={setPreparedMissionsDatesData}
          city={city}
        />
      ) : null}
      {step === STEPS.LOCATION ? (
        <StepLocation
          loading={loading}
          classes={classes}
          postcodesWithLocations={postcodesWithLocations}
          isDistributionZoneExists={isDistributionZoneExists}
          preparedMissionsDatesData={preparedMissionsDatesData}
          selectedPostcodes={selectedPostcodes}
          whitelistedLocations={whitelistedLocations}
          setSelectedPostcodes={setSelectedPostcodes}
          setWhitelistedLocations={setWhitelistedLocations}
          locations={locations}
        />
      ) : null}

      {step === STEPS.RESULTS ? (
        <StepResults
          runSnackBar={runSnackBar}
          data={dataBeforeMissionCreation}
          city={city}
          classes={classes}
          locations={locations}
          cvrPercentiles={cvrPercentiles}
          setData={setDataBeforeMissionCreation}
          isEdit={isEditResultsStep}
          setIsEdit={setIsEditResultsStep}
          setModalProps={setModalProps}
          campaignId={campaign.id}
          skippedLocations={skippedLocations}
        />
      ) : null}

      <ModalV2 {...modalProps} />
    </div>
  )
}

export default injectStyles(styles)(AIRoster)
